import { useState } from 'react';

import { Box, Button, Stack, TextField, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import NavCard from '@/components/wrapped/Card';
import {
  BackLink,
  GroupLabel,
  PanelCard,
  TwoColumn,
} from '@/cam-admin/control-panel/bits';
import { type Call, todayLabel } from '@/cam-admin/control-panel/fixtures';
import SearchField from '@/components/mui/SearchField';

export type CallsIntent = { kind: 'open'; id: string } | { kind: 'log' };

type View = { kind: 'list' } | { kind: 'log' };

// The same wrapped Card the member call list uses — title + when/with as
// subtitle lines, chevron from onClick. No bespoke call row.
//
// titleVariant drops the title from compact's h5 to h6. A call is one row in a
// list of them here, not a card on a phone screen with room around it, and at
// 16px the title outweighed the date and who it was with, which is what a CAM
// scans the list for.
const CallRow = ({ call, onOpen }: { call: Call; onOpen: () => void }) => (
  <NavCard
    variant='compact'
    titleVariant='h6'
    title={call.title}
    subtitle={[
      call.time ? `${call.date} at ${call.time}` : call.date,
      `with ${call.withWho}`,
    ]}
    onClick={onOpen}
  />
);

// Calls: upcoming and past calls. Opening a call shows its detail in a drawer
// (CallDrawer, rendered by ControlPanel). Logging a call is its own flow, opened
// via a jump intent (handover minis / notifications).
const CallsTab = ({
  calls,
  intent,
  onChangeCalls,
  onToast,
  onOpenCall,
  onCreate,
  flat = false,
}: {
  calls: Call[];
  intent?: CallsIntent;
  onChangeCalls: (next: Call[]) => void;
  onToast: (m: string) => void;
  onOpenCall: (id: string) => void;
  // Show a Create call note button in the list, opening the log drawer — the
  // descoping build opts in; the live panel only logs a call via a jump intent.
  onCreate?: () => void;
  // One flat list with no Upcoming / Past grouping (descoping call notes).
  flat?: boolean;
}) => {
  const [view, setView] = useState<View>(
    intent?.kind === 'log' ? { kind: 'log' } : { kind: 'list' },
  );

  // Log-a-call form state.
  const [calendly, setCalendly] = useState('');
  const [fetched, setFetched] = useState(false);
  const [logDate, setLogDate] = useState('');
  const [logTime, setLogTime] = useState('');
  const [logDuration, setLogDuration] = useState('');
  const [logType, setLogType] = useState('');
  const [logWho, setLogWho] = useState('');
  const [logNotes, setLogNotes] = useState('');

  // Pasting a Calendly link prepopulates the metadata fields (all editable).
  const fetchCalendly = () => {
    if (!calendly.trim() || fetched) return;
    setLogDate('Wed 17 Jun');
    setLogTime('10:00');
    setLogDuration('30 min');
    setLogType('Video call');
    setLogWho('Sophie');
    setFetched(true);
    onToast('Call details pulled from the Calendly link');
  };

  const saveLog = () => {
    onChangeCalls([
      {
        id: `new-c${calls.length + 1}`,
        title: logType ? `${logType} notes` : 'Call notes',
        withWho: logWho || 'Sophie',
        date: logDate || todayLabel(),
        time: logTime || '·',
        duration: logDuration || undefined,
        type: logType || undefined,
        notes: logNotes.trim(),
      },
      ...calls,
    ]);
    onToast('Call notes saved. Sophie has been notified');
    setView({ kind: 'list' });
  };

  // ── list ──
  if (view.kind === 'list') {
    const upcoming = calls.filter((c) => c.upcoming);
    const past = calls.filter((c) => !c.upcoming);
    return (
      <Stack spacing={2}>
        {/* Search on the left; the descoping build adds a Create call note
            action on the right. SearchField owns how the search bar looks, the
            wrapper owns its width. */}
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
          spacing={2}
        >
          <Box sx={{ width: 320 }}>
            <SearchField placeholder='Search call notes' />
          </Box>
          {onCreate && (
            <Button
              variant='contained'
              color='primary'
              size='small'
              startIcon={<Icon icon='circle-plus' fontSize={16} />}
              onClick={onCreate}
            >
              Create call note
            </Button>
          )}
        </Stack>
        {flat ? (
          <Stack spacing={1}>
            {calls.map((c) => (
              <CallRow key={c.id} call={c} onOpen={() => onOpenCall(c.id)} />
            ))}
          </Stack>
        ) : (
          <>
            {upcoming.length > 0 && (
              <Stack spacing={1}>
                <GroupLabel>Upcoming</GroupLabel>
                {upcoming.map((c) => (
                  <CallRow
                    key={c.id}
                    call={c}
                    onOpen={() => onOpenCall(c.id)}
                  />
                ))}
              </Stack>
            )}
            <Stack spacing={1}>
              <GroupLabel>Past</GroupLabel>
              {past.map((c) => (
                <CallRow key={c.id} call={c} onOpen={() => onOpenCall(c.id)} />
              ))}
            </Stack>
          </>
        )}
      </Stack>
    );
  }

  // ── log a call ──
  return (
    <Stack spacing={1}>
      <BackLink label='Calls' onClick={() => setView({ kind: 'list' })} />
      <TwoColumn
        main={
          <Stack spacing={2}>
            <PanelCard title='Log a call'>
              <TextField
                fullWidth
                label='Calendly link (optional)'
                placeholder='https://calendly.com/…'
                value={calendly}
                onChange={(e) => setCalendly(e.target.value)}
                onBlur={fetchCalendly}
                helperText='Paste a Calendly link to pull in the call details'
              />
              <Stack direction='row' spacing={2}>
                <TextField
                  fullWidth
                  label='Date'
                  placeholder='e.g. Wed 17 Jun'
                  value={logDate}
                  onChange={(e) => setLogDate(e.target.value)}
                />
                <TextField
                  fullWidth
                  label='Time'
                  placeholder='e.g. 10:00'
                  value={logTime}
                  onChange={(e) => setLogTime(e.target.value)}
                />
              </Stack>
              <Stack direction='row' spacing={2}>
                <TextField
                  fullWidth
                  label='Duration'
                  placeholder='e.g. 30 min'
                  value={logDuration}
                  onChange={(e) => setLogDuration(e.target.value)}
                />
                <TextField
                  fullWidth
                  label='Call type'
                  placeholder='e.g. Video call'
                  value={logType}
                  onChange={(e) => setLogType(e.target.value)}
                />
              </Stack>
              <TextField
                fullWidth
                label='Who was on the call'
                placeholder='e.g. Sophie'
                value={logWho}
                onChange={(e) => setLogWho(e.target.value)}
              />
              <TextField
                fullWidth
                multiline
                minRows={5}
                label='Summary notes'
                value={logNotes}
                onChange={(e) => setLogNotes(e.target.value)}
              />
            </PanelCard>
            <Stack direction='row' spacing={2}>
              <Button
                variant='contained'
                color='primary'
                disabled={!logDate.trim() || !logNotes.trim()}
                onClick={saveLog}
              >
                Save call notes
              </Button>
              <Button
                variant='outlined'
                color='primary'
                onClick={() => setView({ kind: 'list' })}
              >
                Discard
              </Button>
            </Stack>
          </Stack>
        }
        rail={
          <PanelCard title='Linked documents'>
            <Typography variant='body2' color='text.secondary'>
              Documents can be linked once the call notes are saved.
            </Typography>
          </PanelCard>
        }
      />
    </Stack>
  );
};

export default CallsTab;
